<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, sameAs, helpers } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccountFeatures } from 'dashboard/composables/useAccountFeatures';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();
const { hasMarketingCloudTenantId } = useAccountFeatures();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);

// 密码相关字段（可选）
const agentPassword = ref('');
const agentPasswordConfirmation = ref('');
const showPassword = ref(false);
const showPasswordConfirmation = ref(false);
const showPasswordFields = ref(false);

// 自定义密码复杂度验证器
const containsUppercase = helpers.withMessage(
  () => t('AGENT_MGMT.EDIT.FORM.PASSWORD.DETAILS.UPPERCASE'),
  value => !helpers.req(value) || /[A-Z]/.test(value)
);
const containsLowercase = helpers.withMessage(
  () => t('AGENT_MGMT.EDIT.FORM.PASSWORD.DETAILS.LOWERCASE'),
  value => !helpers.req(value) || /[a-z]/.test(value)
);
const containsNumber = helpers.withMessage(
  () => t('AGENT_MGMT.EDIT.FORM.PASSWORD.DETAILS.NUMBER'),
  value => !helpers.req(value) || /[0-9]/.test(value)
);
const containsSpecial = helpers.withMessage(
  () => t('AGENT_MGMT.EDIT.FORM.PASSWORD.DETAILS.SPECIAL'),
  value => !helpers.req(value) || /[!@#$%^&*()_+\-=\[\]{}|']/.test(value)
);

// 动态验证规则：只有显示密码字段时才验证
const rules = computed(() => {
  const baseRules = {
    agentName: { required, minLength: minLength(1) },
    selectedRoleId: { required },
    agentAvailability: { required },
  };

  if (showPasswordFields.value) {
    baseRules.agentPassword = {
      minLength: minLength(8),
      containsUppercase,
      containsLowercase,
      containsNumber,
      containsSpecial,
    };
    baseRules.agentPasswordConfirmation = {
      sameAsPassword: sameAs(agentPassword),
    };
  }

  return baseRules;
});

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
  agentPassword,
  agentPasswordConfirmation,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

// 切换密码字段显示
const togglePasswordFields = () => {
  showPasswordFields.value = !showPasswordFields.value;
  if (!showPasswordFields.value) {
    // 隐藏时清空密码
    agentPassword.value = '';
    agentPasswordConfirmation.value = '';
  }
};

// 切换密码可见性
const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

const togglePasswordConfirmationVisibility = () => {
  showPasswordConfirmation.value = !showPasswordConfirmation.value;
};

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    // 只有在显示密码字段且有输入时才添加密码参数
    if (showPasswordFields.value && agentPassword.value) {
      payload.password = agentPassword.value;
      payload.password_confirmation = agentPasswordConfirmation.value;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    // 从后端响应中提取具体错误消息
    let errorMessage = t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE');
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    }
    useAlert(errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <!-- 修改密码区域（非 SSO 用户且非营销云租户） -->
      <div
        v-if="provider !== 'saml' && !hasMarketingCloudTenantId"
        class="w-full"
      >
        <button
          type="button"
          class="text-sm text-n-brand dark:text-n-brand hover:underline mb-2"
          @click="togglePasswordFields"
        >
          {{
            showPasswordFields
              ? $t('AGENT_MGMT.EDIT.FORM.PASSWORD.CANCEL')
              : $t('AGENT_MGMT.EDIT.FORM.PASSWORD.TOGGLE')
          }}
        </button>

        <div v-if="showPasswordFields" class="flex flex-col gap-4">
          <!-- 密码输入框 -->
          <div class="w-full relative">
            <label :class="{ error: v$.agentPassword.$error }">
              {{ $t('AGENT_MGMT.EDIT.FORM.PASSWORD.LABEL') }}
              <div class="relative">
                <input
                  v-model="agentPassword"
                  :type="showPassword ? 'text' : 'password'"
                  :placeholder="$t('AGENT_MGMT.EDIT.FORM.PASSWORD.PLACEHOLDER')"
                  class="w-full pr-10"
                  @input="v$.agentPassword.$touch"
                />
                <button
                  type="button"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-n-slate-10 hover:text-n-slate-12"
                  @click="togglePasswordVisibility"
                >
                  <Icon
                    :icon="showPassword ? 'i-lucide-eye-off' : 'i-lucide-eye'"
                    size="16"
                  />
                </button>
              </div>
              <!-- 密码验证错误提示 -->
              <div
                v-if="v$.agentPassword.$error"
                class="flex flex-col gap-1 mt-1"
              >
                <span
                  v-if="v$.agentPassword.minLength.$invalid"
                  class="message text-xs"
                >
                  密码至少需要 8 位
                </span>
                <span
                  v-if="v$.agentPassword.containsUppercase.$invalid"
                  class="message text-xs"
                >
                  {{ v$.agentPassword.containsUppercase.$message }}
                </span>
                <span
                  v-if="v$.agentPassword.containsLowercase.$invalid"
                  class="message text-xs"
                >
                  {{ v$.agentPassword.containsLowercase.$message }}
                </span>
                <span
                  v-if="v$.agentPassword.containsNumber.$invalid"
                  class="message text-xs"
                >
                  {{ v$.agentPassword.containsNumber.$message }}
                </span>
                <span
                  v-if="v$.agentPassword.containsSpecial.$invalid"
                  class="message text-xs"
                >
                  {{ v$.agentPassword.containsSpecial.$message }}
                </span>
              </div>
              <!-- 密码规则提示 -->
              <div
                v-else-if="showPasswordFields"
                class="text-xs text-n-slate-11 dark:text-n-slate-7 mt-1"
              >
                {{ $t('AGENT_MGMT.EDIT.FORM.PASSWORD.REQUIREMENTS') }}
              </div>
            </label>
          </div>

          <!-- 确认密码输入框 -->
          <div class="w-full relative">
            <label :class="{ error: v$.agentPasswordConfirmation.$error }">
              {{ $t('AGENT_MGMT.EDIT.FORM.PASSWORD_CONFIRMATION.LABEL') }}
              <div class="relative">
                <input
                  v-model="agentPasswordConfirmation"
                  :type="showPasswordConfirmation ? 'text' : 'password'"
                  :placeholder="
                    $t('AGENT_MGMT.EDIT.FORM.PASSWORD_CONFIRMATION.PLACEHOLDER')
                  "
                  class="w-full pr-10"
                  @input="v$.agentPasswordConfirmation.$touch"
                />
                <button
                  type="button"
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-n-slate-10 hover:text-n-slate-12"
                  @click="togglePasswordConfirmationVisibility"
                >
                  <Icon
                    :icon="
                      showPasswordConfirmation
                        ? 'i-lucide-eye-off'
                        : 'i-lucide-eye'
                    "
                    size="16"
                  />
                </button>
              </div>
              <span v-if="v$.agentPasswordConfirmation.$error" class="message">
                {{ $t('AGENT_MGMT.EDIT.FORM.PASSWORD_CONFIRMATION.ERROR') }}
              </span>
            </label>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isUpdating"
          :is-loading="uiFlags.isUpdating"
        />
      </div>
    </form>
  </div>
</template>
